import { FormControl, FormControlProps, FormErrorMessage, FormHelperText, FormLabel } from '@chakra-ui/react'
import { ReactNode } from 'react'

export interface IFormFieldProps extends Omit<FormControlProps, 'label'> {
  /**
   * Rótulo em pt-BR. **Sem dois-pontos no fim e sem `*` digitado** — a obrigatoriedade é
   * `isRequired`, que desenha o asterisco sozinho.
   */
  label?: ReactNode
  /**
   * Mensagem de erro do campo. Um array (o que a API devolve em `errors`, e o que o
   * `toFormErrors` normaliza) sai uma linha por mensagem.
   *
   * Passar erro já liga o `isInvalid` — a página não precisa manter os dois em sincronia.
   */
  error?: string | string[] | null
  /** Texto de apoio abaixo do controle. Cede a vez ao erro, para não empilhar duas mensagens. */
  helperText?: ReactNode
  /**
   * Só quando o `FormControl` não consegue associar o controle sozinho: dois inputs no mesmo
   * campo, um grupo de checkboxes. O `<SearchableSelect>` se associa sozinho — ele lê o `id` do
   * `FormControl` e o usa como `inputId`, então não precisa mais deste `htmlFor` escrito à mão.
   */
  htmlFor?: string
  children: ReactNode
}

/**
 * A regra de "este campo está em erro" — uma só, porque o `RichTextField` também precisa dela
 * para pintar a borda do editor. Com duas cópias, um `error={['']}` pintava a borda de vermelho
 * sem mostrar mensagem nenhuma.
 */
export const fieldErrorMessages = (error?: string | string[] | null): string[] =>
  (Array.isArray(error) ? error : [error]).filter(Boolean) as string[]

/**
 * Campo de formulário do painel: rótulo + controle + erro, com um `FormControl` de verdade em volta.
 *
 * O `FormControl` não é decoração — é ele que dá `htmlFor` automático, propaga
 * `isInvalid`/`isDisabled`/`isRequired` para o controle e faz o `FormErrorMessage` aparecer. Metade
 * dos rótulos do painel estava fora de um, e por isso 85 deles carregavam `mt={4}` na mão: sem
 * `FormControl` não havia nada para dar o espaçamento.
 *
 * **Obrigatoriedade tem uma convenção só: `isRequired`.** Ela desenha o asterisco *e* marca o input
 * como `required` para o browser, via contexto do Chakra. Havia quatro convenções concorrentes — o
 * `isRequired`, um ` *` digitado no texto, o `required` nativo sem marcador visual nenhum, e nada —
 * e duas delas apareciam com cor e posição diferentes na mesma tela.
 *
 * **Erro tem uma convenção só: a prop `error`.** Havia quatro — `isInvalid` + `FormErrorMessage`,
 * um `<Text color="red.500">` solto em seis estilos diferentes, só toast, e só a validação nativa
 * do browser.
 *
 * Props de layout (`w`, `flex`, `gridColumn`, `mb`) vão para o `FormControl`, então o `<Box>` que
 * costumava embrulhar o campo some junto.
 */
export default function FormField({
  label,
  error,
  helperText,
  htmlFor,
  children,
  isInvalid,
  ...rest
}: IFormFieldProps) {
  const messages = fieldErrorMessages(error)
  const hasError = messages.length > 0

  return (
    <FormControl isInvalid={isInvalid ?? hasError} {...rest}>
      {label && <FormLabel htmlFor={htmlFor}>{label}</FormLabel>}

      {children}

      {hasError
        ? messages.map((message, index) => <FormErrorMessage key={index}>{message}</FormErrorMessage>)
        : helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  )
}
